import AlertDialog from '@seller-hermes/features/ui-v2/AlertDialog';
import { Button } from '@seller-hermes/features/ui-v2/Button';
import {
  useStoreActions,
  useStoreState,
} from '@seller-hermes/hooks/easy-peasy';
import { addDays, addMonths } from 'date-fns';
import { HelpCircle } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export function SupportInfoPopup() {
  const { t } = useTranslation('support-info-popup');
  const user = useStoreState((state) => state.login.sellerGroupUser.user);
  const { feedbackDialogOpen } = useStoreState(
    (state) => state.userFeedbackDialog,
  );

  const { supportInfoDialogOpen, supportInfoDialogShouldOpenAgainAt } =
    useStoreState((state) => state.supportInfoPopup);
  const { setSupportInfoDialogOpen, setSupportInfoDialogShouldOpenAgainAt } =
    useStoreActions((actions) => actions.supportInfoPopup);
  const navigate = useNavigate();

  const shouldShowDialog = useMemo(() => {
    if (feedbackDialogOpen) return false;
    if (!supportInfoDialogOpen) return false;

    if (
      supportInfoDialogShouldOpenAgainAt &&
      new Date(supportInfoDialogShouldOpenAgainAt) > new Date()
    ) {
      return false;
    }

    if (user?.created_at) {
      return addMonths(new Date(user.created_at), 3) < new Date();
    }

    return true;
  }, [
    supportInfoDialogOpen,
    supportInfoDialogShouldOpenAgainAt,
    user?.created_at,
    feedbackDialogOpen,
  ]);

  // scheduling is handled in UserFeedbackDialog to avoid hook ordering issues during logout

  return (
    <AlertDialog
      open={shouldShowDialog}
      onOpenChange={setSupportInfoDialogOpen}
    >
      <AlertDialog.Content className="max-w-[720px]">
        <AlertDialog.Header>
          <AlertDialog.Title>{t('title')}</AlertDialog.Title>
          <AlertDialog.Description>{t('description')}</AlertDialog.Description>
        </AlertDialog.Header>

        <div className="flex flex-col gap-y-6">
          <section className="rounded-md border p-4">
            <h3 className="mb-2 text-base font-medium">{t('what.title')}</h3>
            <ul className="list-disc pl-6 text-sm">
              <li>{t('what.items.openTickets')}</li>
              <li>{t('what.items.trackTickets')}</li>
              <li>{t('what.items.knowledgeBase')}</li>
            </ul>
          </section>

          <section className="rounded-md border p-4">
            <h3 className="mb-2 text-base font-medium">{t('how.title')}</h3>
            <ul className="list-disc pl-6 text-sm">
              <li>
                {t('how.items.widgetPrefix')}{' '}
                <span className="inline-flex items-center align-middle">
                  <HelpCircle className="h-4 w-4" />
                  <span>)</span>
                </span>
              </li>
              <li>
                {t('how.items.directLinkPrefix')}{' '}
                <a
                  href="http://suporte.abbiamolog.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  {t('how.items.directLinkText')}
                </a>
              </li>
              <li>
                <div className="flex flex-col gap-y-1 text-sm">
                  <span>{t('how.items.openWidgetNow')}</span>
                  <Button
                    size="tight"
                    onClick={() => {
                      setSupportInfoDialogOpen(false);
                      navigate('/support/tickets');
                    }}
                    className="w-fit"
                    type="button"
                  >
                    {t('how.items.openWidgetButton')}
                  </Button>
                </div>
              </li>
            </ul>
          </section>

          <section className="rounded-md border p-4">
            <h3 className="mb-2 text-base font-medium">
              {t('solutions.title')}
            </h3>
            <p className="text-muted-foreground text-sm">
              {t('solutions.desc')}
            </p>
            <div className="mt-2">
              <Button asChild>
                <a
                  href="https://suporte.abbiamolog.com/support/solutions"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('solutions.cta')}
                </a>
              </Button>
            </div>
          </section>
        </div>

        <AlertDialog.Footer>
          <div className="flex w-full justify-between gap-x-2">
            <Button
              variant="link"
              onClick={() => {
                // closes dialog and set to reopen in 3 months
                setSupportInfoDialogOpen(false);
                setSupportInfoDialogShouldOpenAgainAt(
                  addMonths(new Date(), 3).toISOString(),
                );
              }}
              type="button"
              className="text-muted-foreground hover:text-foreground underline"
            >
              {t('footer.dontAskAgain')}
            </Button>

            <div className="flex gap-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  // closes dialog and set to reopen in 2 days
                  setSupportInfoDialogOpen(false);
                  setSupportInfoDialogShouldOpenAgainAt(
                    addDays(new Date(), 2).toISOString(),
                  );
                }}
                type="button"
              >
                {t('footer.remindLater')}
              </Button>
              <Button asChild>
                <a
                  href="http://suporte.abbiamolog.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('footer.openHelpCenter')}
                </a>
              </Button>
            </div>
          </div>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );
}

export default SupportInfoPopup;
